import { type FC, type ReactNode, useMemo } from "react";

import { Box, Flex } from "@mantine/core";
import { Alert, Button, Modal } from "@snapchat/snap-design-system";

import {
    type ExclusiveLensStatus,
    type MyLensesLens,
    type SurfaceType,
    Visibility,
} from "../../../gql-generated/types";
import { LENS_PLUS_LEARN_MORE_LINK_URL } from "../../../lens-creator-payout/constants";
import { LensPreview } from "../../../lens-preview/components";
import { type LensPreviewStateHook } from "../../../lens-preview/hooks";
import { VISIBILITY_OPTION_MAP } from "../../../lens-visibility/components";
import { type LensVisibilityConfig, makeExclusiveLensVisibilityConfig } from "../../../lens-visibility/hooks";
import { Flexbox } from "../../../shared/components";
import { SubmissionQuotaContext } from "../../../shared/components/submission-quota/SubmissionQuotaContext";
import { SubmissionQuotaInline } from "../../../shared/components/submission-quota/SubmissionQuotaInline";
import { type ModalProps } from "../../../shared/hooks";
import { MonetizationSection } from "../../../submit/components/shared/MonetizationSection";

import styles from "./PublishModal.module.css";
import {
    LensNameHeading,
    VisibilityHeading,
    VisibilityOptionsWrapper,
    VisibilitySectionWrapper,
    alertWithBackground,
    spectaclesVisibilityNotice,
    visibilityOptionWithBorder,
} from "./PublishModal.styles";
import {
    AiClipsAlertMessage,
    AiClipsAlertTitle,
    Cancel,
    ErrorTitle,
    LearnMoreText,
    LensPlusOnlyVisibilityOptionsNotice,
    PublicationWarning,
    PublicationWarningText,
    PublishModalTitle,
    SpectaclesOnlyVisibilityNotice,
    SpectaclesOnlyVisibilityOptionsNotice,
    UpdateModalTitle,
    UpdatingWarning,
    UpdatingWarningText,
    VisibilityTitle,
} from "./messages.generated";
import { usePublishModalState } from "./usePublishModalState";
import { usePublishSubmissionQuotaGuard } from "./usePublishSubmissionQuotaGuard";

// Flattened props interface for better usability
type PublishModalProps = ModalProps & {
    // Lens data props
    lensName: string;
    lensIconUrl?: string;
    lensSurfaceType: readonly SurfaceType[];
    lensHasMusic?: boolean;
    lensHasLyrics?: boolean;
    lensId: string;
    lensForUpdate?: Pick<MyLensesLens, "name" | "distributionsList" | "lensCreatorPayoutEligibility">;
    isNewLensPublication: boolean;

    // Visibility props
    visibilityConfig: LensVisibilityConfig;
    currentVisibility: Visibility | undefined;
    onVisibilityChange: (visibility: Visibility) => void;

    // Creator payout props
    creatorPayoutEnrolled?: boolean;
    onCreatorPayoutEnrolledChange?: (enrolled: boolean) => void;

    // Exclusive Lens props
    exclusiveLensStatus: ExclusiveLensStatus;

    // Action props
    actionButtonText: ReactNode;
    onAction: () => void;
    actionIsPending: boolean;
    actionIsDisabled: boolean;

    isAIClip?: boolean;

    // Other props
    lensPreviewProps: LensPreviewStateHook;
    errorMessage?: string | null;
};

const LEARN_MORE_URL = "https://businesshelp.snapchat.com/s/article/lens-profile";

export const PublishModal: FC<PublishModalProps> = ({
    // Lens data props
    lensName,
    lensIconUrl,
    lensSurfaceType,
    lensHasMusic,
    lensHasLyrics,
    lensId,
    lensForUpdate,
    isNewLensPublication,

    // Visibility props
    visibilityConfig,
    currentVisibility,
    onVisibilityChange,

    // Exclusive Lens props
    exclusiveLensStatus,

    // Creator payout props
    creatorPayoutEnrolled,
    onCreatorPayoutEnrolledChange,

    // Action props
    actionButtonText,
    onAction,
    actionIsPending,
    actionIsDisabled,

    // AI Clips props
    isAIClip,

    // Other props
    lensPreviewProps,
    visible: isModalVisible,
    onCancel,
    errorMessage,
}) => {
    // Extract complex state logic into custom hook
    const {
        isSpectaclesOnly,
        isSnapchatOnly,
        username,
        isMyLensesCreatorPayoutEnabled,
        isMonetizationDisabled,
        submitLensState,
        isExclusiveLensChecked,
        setIsExclusiveLens,
        previewMediaItems,
        regularSubmissionQuota,
        lensPlusSubmissionQuota,
        refetchSubmissionQuotas,
    } = usePublishModalState({
        surfaceType: lensSurfaceType,
        lensForUpdate: lensForUpdate,
        visibility: currentVisibility,
        lensOrSubmissionId: lensId,
    });
    const requiresSubmissionQuota =
        isNewLensPublication && currentVisibility !== Visibility.VisibilityDontPublishToSnapchat;
    const usesLensPlusQuota = submitLensState.isExclusiveLens || Boolean(isAIClip);
    const {
        activeSubmissionQuota,
        activeSubmissionQuotaContext,
        isChecking: isQuotaCheckPending,
        isBlocked: isActiveSubmissionQuotaExhausted,
        checkBeforePublish,
    } = usePublishSubmissionQuotaGuard({
        requiresSubmissionQuota,
        usesLensPlusQuota,
        regularSubmissionQuota,
        lensPlusSubmissionQuota,
        refetchSubmissionQuotas,
        isModalVisible,
    });
    const isRegularSubmissionQuotaMuted = submitLensState.isExclusiveLens;
    const displayedSubmissionQuota = isRegularSubmissionQuotaMuted ? regularSubmissionQuota : activeSubmissionQuota;
    const displayedSubmissionQuotaContext = isRegularSubmissionQuotaMuted
        ? SubmissionQuotaContext.Regular
        : activeSubmissionQuotaContext;

    const handleVisibilityOptionClick = (visibility: Visibility) => {
        if (isQuotaCheckPending) {
            return;
        }
        onVisibilityChange(visibility);
    };

    const enhancedVisibilityConfig = useMemo(() => {
        if (!submitLensState.isExclusiveLens && !isAIClip) {
            return visibilityConfig;
        }

        return makeExclusiveLensVisibilityConfig(visibilityConfig, <LensPlusOnlyVisibilityOptionsNotice />);
    }, [visibilityConfig, submitLensState.isExclusiveLens, isAIClip]);

    const isActionDisabled = (isSpectaclesOnly ? false : actionIsDisabled) || isActiveSubmissionQuotaExhausted;
    const isActionPending = actionIsPending || isQuotaCheckPending;

    const handleAction = async () => {
        if (isActiveSubmissionQuotaExhausted) {
            return;
        }

        if (requiresSubmissionQuota) {
            const shouldBlockPublication = await checkBeforePublish();
            if (shouldBlockPublication) {
                return;
            }
        }

        if (isSpectaclesOnly || submitLensState.isExclusiveLens || isAIClip) {
            // Spectacles, exclusive, and AI clip lenses must have public visibility for distribution
            onVisibilityChange(Visibility.VisibilityPublic);
        }
        onAction();
    };

    return (
        <Modal
            data-testid="publish-modal"
            title={lensForUpdate ? <UpdateModalTitle /> : <PublishModalTitle />}
            visible={isModalVisible}
            onCancel={onCancel}
            width={760}
            footer={
                <div className={styles.footer}>
                    <div className={styles.footerQuota}>
                        {requiresSubmissionQuota ? (
                            <SubmissionQuotaInline
                                quota={displayedSubmissionQuota}
                                context={displayedSubmissionQuotaContext}
                                isMuted={isRegularSubmissionQuotaMuted}
                            />
                        ) : null}
                    </div>
                    <div className={styles.footerActions}>
                        <Button onClick={onCancel} isDisabled={isActionPending}>
                            <Cancel />
                        </Button>
                        <Button
                            type="primary"
                            onClick={() => void handleAction()}
                            isPending={isActionPending}
                            isDisabled={isActionDisabled}
                        >
                            {actionButtonText}
                        </Button>
                    </div>
                </div>
            }
        >
            <Flexbox justifyContent="space-between">
                <Flexbox flexDirection="column" alignItems="center">
                    <LensPreview
                        {...lensPreviewProps}
                        logoSrc={lensIconUrl}
                        isReadOnly={true}
                        isLargePreview={true}
                        surfaceType={lensSurfaceType}
                    />
                    <LensNameHeading>{lensName}</LensNameHeading>
                </Flexbox>
                <VisibilitySectionWrapper>
                    {!isSpectaclesOnly && (
                        <Flex direction="column" gap="xs">
                            <Alert
                                className={alertWithBackground}
                                message={
                                    lensForUpdate ? (
                                        <UpdatingWarningText lensName={lensForUpdate.name} />
                                    ) : (
                                        <PublicationWarningText
                                            username={username}
                                            learnMore={
                                                <a href={LEARN_MORE_URL} target="_blank" rel="noreferrer">
                                                    <LearnMoreText />
                                                </a>
                                            }
                                        />
                                    )
                                }
                                status="warning"
                                title={
                                    lensForUpdate ? (
                                        <UpdatingWarning lensName={lensForUpdate.name} />
                                    ) : (
                                        <PublicationWarning username={username} />
                                    )
                                }
                            />
                            {isAIClip && (
                                <Alert
                                    className={alertWithBackground}
                                    status="warning"
                                    title={<AiClipsAlertTitle />}
                                    message={
                                        <AiClipsAlertMessage
                                            learnMore={
                                                <a
                                                    href={LENS_PLUS_LEARN_MORE_LINK_URL}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    <LearnMoreText />
                                                </a>
                                            }
                                        />
                                    }
                                />
                            )}
                        </Flex>
                    )}

                    <>
                        <VisibilityOptionsWrapper>
                            <VisibilityHeading>
                                <VisibilityTitle />
                            </VisibilityHeading>
                            {isSpectaclesOnly && (
                                <p className={spectaclesVisibilityNotice}>
                                    <SpectaclesOnlyVisibilityNotice />
                                </p>
                            )}

                            {enhancedVisibilityConfig.communityDistributionOptions?.map((option) => {
                                const VisibilityOption = VISIBILITY_OPTION_MAP[option];
                                const isSelected = option === currentVisibility;

                                // For Spectacles-only lenses, disable all options with Spectacles message
                                const spectaclesDisableOption = {
                                    option,
                                    message: <SpectaclesOnlyVisibilityOptionsNotice />,
                                };

                                const disabledVisibilityOptionConfig = isSpectaclesOnly
                                    ? spectaclesDisableOption
                                    : enhancedVisibilityConfig.disabledOptions?.[option];

                                return (
                                    <VisibilityOption
                                        className={visibilityOptionWithBorder}
                                        key={option}
                                        setVisibility={() => {
                                            handleVisibilityOptionClick(option);
                                        }}
                                        selected={isSelected}
                                        disabledVisibilityOptionConfig={disabledVisibilityOptionConfig}
                                    />
                                );
                            })}
                        </VisibilityOptionsWrapper>
                        <Flex gap="md" direction="column">
                            {!isSpectaclesOnly && !isAIClip && isMyLensesCreatorPayoutEnabled && (
                                <MonetizationSection
                                    // LensCreatorPayout props
                                    lensCreatorPayoutEnrolled={creatorPayoutEnrolled}
                                    setLensCreatorPayoutEnrolled={onCreatorPayoutEnrolledChange}
                                    lensForUpdate={lensForUpdate}
                                    visibility={currentVisibility}
                                    // ExclusiveLensToggle props
                                    hasMusic={lensHasMusic}
                                    hasLyrics={lensHasLyrics}
                                    previewMediaItems={previewMediaItems}
                                    exclusiveLensStatus={exclusiveLensStatus}
                                    onExclusiveLensChange={setIsExclusiveLens}
                                    canToggleExclusiveLens={true}
                                    // Shared props
                                    disabled={isQuotaCheckPending || Boolean(isMonetizationDisabled)}
                                    isExclusiveLensFeatureEnabled={isSnapchatOnly}
                                    isMyLensesCreatorPayoutEnabled={isMyLensesCreatorPayoutEnabled}
                                    isExclusiveLensChecked={isExclusiveLensChecked}
                                    isAIClip={isAIClip}
                                    lensPlusSubmissionQuota={requiresSubmissionQuota ? lensPlusSubmissionQuota : null}
                                />
                            )}

                            {errorMessage && (
                                <Box mt="md">
                                    <Alert status="error" title={<ErrorTitle />} description={errorMessage} />
                                </Box>
                            )}
                        </Flex>
                    </>
                </VisibilitySectionWrapper>
            </Flexbox>
        </Modal>
    );
};
